// =============================================================
// DamLibrary.jsx — Digital Asset Management: the main library view.
//
// Collect & Store  → upload (DamUpload)
// Search & Organise → collections sidebar, type + tag filters, AI/keyword search
// Use & Deploy      → single + bulk download
//
// Part of the removable DAM module. Removing the module = delete
// frontend/src/pages/dam/ and its wiring in App.jsx + constants/nav.js.
// =============================================================
import { useState, useEffect, useCallback } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { api } from '../../api/client';
import { useUser } from '../../context/UserContext';
import { Btn, Icon, Spinner, EmptyState, Chip, Badge, useToast, PageHeader } from '../../components/ui';
import { formatBytes, categoryOf, iconForCategory, isImageType, downloadAsset, fetchIntake } from './damApi';
import { DamUpload } from './DamUpload';
import { DamIntakeQueue } from './DamIntakeQueue';
import { DamAssetModal } from './DamAssetModal';
import { DamShareModal } from './DamShareModal';
import { DamConnectorModal } from './DamConnectorModal';
import DamResetModal from './DamResetModal';
import { DamDownloadModal } from './DamDownloadModal';
import { formatInstant } from '../../utils/format';

// Per-request budget for the library's mount-time loads. The shared client
// defaults to 10s, which the asset list can legitimately exceed — it reads the
// whole ready set and mints a preview + thumbnail read-SAS for every row, and on
// a cold Function App the first call also pays the runtime + SQL pool start-up.
// A short budget turned that into an error toast and an empty grid on a page
// that was about to answer.
const LOAD_TIMEOUT_MS = 30000;

// The collection sidebar carries three pseudo-values beside the real ids. Kept
// in one predicate because every modal that preselects a collection has to
// exclude all of them, and a list that grew a fourth entry in four places is how
// one of them ends up sending "intake" to the API as a collection id.
const PSEUDO_COLLECTIONS = new Set(['all', 'uncollected', 'intake']);
const isRealCollection = (id) => !!id && !PSEUDO_COLLECTIONS.has(id);

const TYPE_FILTERS = [
  { id: '', label: 'All types' },
  { id: 'image', label: 'Images' },
  { id: 'video', label: 'Video' },
  { id: 'audio', label: 'Audio' },
  { id: 'document', label: 'Documents' },
  { id: 'design', label: 'Design' },
];

const ORIENTATION_FILTERS = [
  { value: '', label: 'Any orientation' },
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'square', label: 'Square' },
];
const DATE_FILTERS = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Past 24 hours' },
  { value: '7', label: 'Past 7 days' },
  { value: '30', label: 'Past 30 days' },
  { value: '365', label: 'Past year' },
];
const RAW_EDITED_FILTERS = [
  { value: '', label: 'Raw & edited' },
  { value: 'raw', label: 'Raw only' },
  { value: 'edited', label: 'Edited only' },
  { value: 'unspecified', label: 'Unclassified' },
];

export function DamLibrary() {
  const user = useUser();
  const isAdmin = user?.role === 'Admin';
  const { toast } = useToast();

  const [needsInit, setNeedsInit] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const [collections, setCollections] = useState([]);
  const [tags, setTags] = useState([]);
  const [uploaders, setUploaders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');                 // debounced search
  const [searchMode, setSearchMode] = useState('loose'); // 'loose' | 'strict'
  const [collectionId, setCollectionId] = useState('all');
  const [type, setType] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [orientation, setOrientation] = useState('');
  const [addedWithin, setAddedWithin] = useState('');
  const [uploadedBy, setUploadedBy] = useState('');
  const [rawEdited, setRawEdited] = useState('');

  // UI state
  const [showUpload, setShowUpload] = useState(false);
  const [showUploadLinks, setShowUploadLinks] = useState(false);
  const [showConnectors, setShowConnectors] = useState(false);
  const [openAssetId, setOpenAssetId] = useState(null);
  const [downloadAssetTarget, setDownloadAssetTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [newCollectionName, setNewCollectionName] = useState('');
  const [showNewCollection, setShowNewCollection] = useState(false);

  // Intake triage — files an ingest brought in that nobody has filed. They are a
  // separate VIEW rather than another collection filter: they are not in the
  // library at all, so a grid of them filtered by tag/type/orientation would be
  // answering a question about a set the grid does not otherwise show.
  const [intake, setIntake] = useState(null);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // SharePoint large-file dropbox (admin) — status + manual scan
  const [dropboxStatus, setDropboxStatus] = useState(null);
  const [showDropbox, setShowDropbox] = useState(false);
  // Whole-library reset (admin) — soft-deletes every row AND deletes the bytes.
  // It sits beside the other admin controls rather than behind a nav entry: it
  // is a lifecycle action on this page's own data, and what protects it is the
  // typed confirmation in the dialog, not obscurity.
  const [showReset, setShowReset] = useState(false);
  const [scanning, setScanning] = useState(false);

  // Debounce the search box → q
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Refresh just the collections list. Kept separate (and resilient) so
  // creating/deleting a collection reflects immediately even if the tag or
  // uploader lookups are unavailable.
  const loadCollections = useCallback(async () => {
    const c = await api.get('dam/collections', { timeout: LOAD_TIMEOUT_MS });
    if (c.needsInit) { setNeedsInit(true); return; }
    setCollections(c.collections || []);
    // The queue's badge rides on the collections call rather than costing a
    // request of its own — it is a count the sidebar needs on every load, and a
    // queue nobody can see the size of is a queue nobody clears.
    setPendingCount(c.pendingCount || 0);
  }, []);

  // Load the whole sidebar. Each fetch is independent: a failure of the tag or
  // uploader lookup must NOT prevent the collections list from rendering (a
  // shared Promise.all previously blanked the entire sidebar — including a
  // just-created collection — whenever any one call rejected).
  const loadSidebar = useCallback(async () => {
    const [c, t, u] = await Promise.allSettled([
      api.get('dam/collections', { timeout: LOAD_TIMEOUT_MS }),
      api.get('dam/tags',        { timeout: LOAD_TIMEOUT_MS }),
      api.get('dam/uploaders',   { timeout: LOAD_TIMEOUT_MS }),
    ]);
    if (c.status === 'fulfilled') {
      if (c.value.needsInit) { setNeedsInit(true); return; }
      setCollections(c.value.collections || []);
      setPendingCount(c.value.pendingCount || 0);
    } else { toast.error(c.reason?.message || 'Could not load collections'); }
    if (t.status === 'fulfilled') setTags(t.value.tags || []);
    if (u.status === 'fulfilled') setUploaders(u.value.uploaders || []);
  }, [toast]);

  const loadIntake = useCallback(async () => {
    setIntakeLoading(true);
    try { setIntake(await fetchIntake()); }
    catch (e) { toast.error(e.message); }
    finally { setIntakeLoading(false); }
  }, [toast]);

  const loadAssets = useCallback(async () => {
    // The queue is its own read; loading the library grid behind it would be a
    // request whose result is never rendered.
    if (collectionId === 'intake') { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) { params.set('q', q); if (searchMode === 'strict') params.set('mode', 'strict'); }
      if (type) params.set('type', type);
      if (activeTag) params.set('tag', activeTag);
      if (orientation) params.set('orientation', orientation);
      if (addedWithin) params.set('addedWithin', addedWithin);
      if (uploadedBy) params.set('uploadedBy', uploadedBy);
      if (rawEdited) params.set('rawEdited', rawEdited);
      if (collectionId === 'uncollected') params.set('collectionId', 'uncollected');
      else if (collectionId !== 'all') params.set('collectionId', collectionId);
      const res = await api.get(`dam/assets?${params.toString()}`, { timeout: LOAD_TIMEOUT_MS });
      if (res.needsInit) { setNeedsInit(true); setAssets([]); return; }
      setNeedsInit(false);
      setAssets(res.assets || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [q, searchMode, type, activeTag, orientation, addedWithin, uploadedBy, rawEdited, collectionId, toast]);

  // Dropbox status (admin only) — silent: a missing/uninitialized dropbox never
  // disrupts the library, it just leaves the panel hidden.
  const loadDropboxStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('dam/dropbox/status', { timeout: LOAD_TIMEOUT_MS });
      if (res && !res.needsInit) setDropboxStatus(res);
    } catch { /* ignore — dropbox is an optional add-on */ }
  }, [isAdmin]);

  const scanDropbox = async () => {
    setScanning(true);
    try {
      const res = await api.post('dam/dropbox/scan', {});
      if (res.skipped) {
        toast.error(`Dropbox not scanned: ${res.skipped}`);
      } else {
        const c = res.counts || {};
        toast.success(`Dropbox: ${c.ingested || 0} imported, ${c.started || 0} copying, ${c.skippedSyncing || 0} still syncing`);
      }
      await loadDropboxStatus();
      await loadAssets();
    } catch (e) { toast.error(e.message); }
    finally { setScanning(false); }
  };

  useEffect(() => { loadSidebar(); }, [loadSidebar]);
  useEffect(() => { loadAssets(); }, [loadAssets]);
  useEffect(() => { if (collectionId === 'intake') loadIntake(); }, [collectionId, loadIntake]);
  useEffect(() => { loadDropboxStatus(); }, [loadDropboxStatus]);

  const initialize = async () => {
    setInitializing(true);
    try {
      await api.post('dam/admin/migrate', {});
      toast.success('Digital Assets initialized');
      setNeedsInit(false);
      await loadSidebar();
      await loadAssets();
    } catch (e) {
      toast.error(e.status === 401 ? 'An admin must initialize this module.' : e.message);
    } finally { setInitializing(false); }
  };

  const createCollection = async () => {
    const name = newCollectionName.trim();
    if (!name) { toast.error('Enter a collection name'); return; }
    try {
      await api.post('dam/collections', { name });
      setNewCollectionName(''); setShowNewCollection(false);
      // Refresh only the collections list so the new one shows immediately,
      // independent of the (heavier) tag/uploader lookups.
      await loadCollections();
      toast.success('Collection created');
    } catch (e) { toast.error(e.message); }
  };

  const deleteCollection = async (c) => {
    if (!window.confirm(`Delete collection "${c.name}"? Its assets stay in the library, just uncollected.`)) return;
    try {
      await api.del(`dam/collections/${c.collectionID}`);
      if (collectionId === c.collectionID) setCollectionId('all');
      await loadCollections();
      await loadAssets();
    } catch (e) { toast.error(e.message); }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const bulkDownload = async () => {
    const ids = [...selected];
    for (const id of ids) {
      try { await downloadAsset(id); } catch (e) { toast.error(e.message); }
    }
  };

  // Single-asset download: images open the size chooser; everything else
  // downloads the original immediately (there's nothing to resize).
  const handleDownload = (asset) => {
    if (isImageType(asset.fileType, asset.mimeType)) {
      setDownloadAssetTarget(asset);
    } else {
      downloadAsset(asset.assetID).catch(e => toast.error(e.message));
    }
  };

  const anyFilterActive = orientation || addedWithin || uploadedBy || rawEdited;
  const clearFilters = () => { setOrientation(''); setAddedWithin(''); setUploadedBy(''); setRawEdited(''); };

  // ── Not-initialized screen ─────────────────────────────────────────────────
  if (needsInit) {
    return (
      <div style={{ padding: space.xxl }}>
        <PageHeader title="Digital Assets" subtitle="Brand & marketing asset library" icon="image" />
        <div style={{ marginTop: space.xxl }}>
          <EmptyState icon="image" title="Set up the Digital Assets library"
            message={isAdmin
              ? 'Create the storage tables for this module to get started. This is a one-time step.'
              : 'This module has not been set up yet. Ask an admin to initialize it.'}
            action={isAdmin
              ? <Btn onClick={initialize} loading={initializing}><Icon name="sparkles" size={14} /> Initialize module</Btn>
              : null} />
        </div>
      </div>
    );
  }

  const totalCount = assets.length;

  return (
    <div style={{ padding: space.xxl }}>
      <PageHeader title="Digital Assets" subtitle="Brand & marketing asset library" icon="image"
        actions={<>
          {isAdmin && (
            <Btn variant="secondary" onClick={() => setShowDropbox(v => !v)}><Icon name="folder" size={15} /> Dropbox</Btn>
          )}
          {isAdmin && (
            <Btn variant="secondary" onClick={() => setShowConnectors(true)}><Icon name="sparkles" size={15} /> Connect to Claude</Btn>
          )}
          {isAdmin && (
            <Btn variant="secondary" onClick={() => setShowReset(true)} style={{ color: B.red, borderColor: B.red }}>
              <Icon name="trash" size={15} color={B.red} /> Reset library
            </Btn>
          )}
          <Btn variant="secondary" onClick={() => setShowUploadLinks(true)}><Icon name="external" size={15} /> Upload links</Btn>
          <Btn onClick={() => setShowUpload(true)}><Icon name="upload" size={15} /> Upload</Btn>
        </>} />

      {/* Large-file dropbox (admin) — drop huge files into a synced SharePoint
          folder; they import server-side and move to a Processed subfolder. */}
      {isAdmin && showDropbox && (
        <DropboxPanel status={dropboxStatus} scanning={scanning} onScan={scanDropbox} />
      )}

      <div style={{ display: 'flex', gap: space.xl, marginTop: space.lg, alignItems: 'flex-start' }}>
        {/* Sidebar */}
        <aside style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <div>
            <div style={sidebarHeader}>
              <span>Collections</span>
              <button type="button" onClick={() => setShowNewCollection(v => !v)} style={miniBtn} title="New collection">
                <Icon name="add" size={14} color={B.navy} />
              </button>
            </div>
            {showNewCollection && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                <input autoFocus value={newCollectionName} onChange={e => setNewCollectionName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') createCollection(); if (e.key === 'Escape') setShowNewCollection(false); }}
                  placeholder="Collection name" style={miniInput} />
                <button type="button" onClick={createCollection} style={miniBtn}><Icon name="check" size={14} color={B.green} /></button>
              </div>
            )}
            <SidebarItem active={collectionId === 'all'} onClick={() => setCollectionId('all')} icon="grid" label="All assets" />
            {/* Rendered only when there IS something to file, and above
                Uncollected because the two are easily confused: these files are
                not in the library at all, and nobody has looked at them. */}
            {pendingCount > 0 && (
              <SidebarItem active={collectionId === 'intake'} onClick={() => setCollectionId('intake')}
                icon="emailInbox" label="Needs filing" count={pendingCount} />
            )}
            <SidebarItem active={collectionId === 'uncollected'} onClick={() => setCollectionId('uncollected')} icon="folder" label="Uncollected" />
            {collections.map(c => (
              <SidebarItem key={c.collectionID} active={collectionId === c.collectionID}
                onClick={() => setCollectionId(c.collectionID)} icon="folderOpen" label={c.name}
                count={c.assetCount} onDelete={() => deleteCollection(c)} />
            ))}
          </div>

          <div>
            <div style={sidebarHeader}><span>File type</span></div>
            {TYPE_FILTERS.map(tf => (
              <SidebarItem key={tf.id} active={type === tf.id} onClick={() => setType(tf.id)}
                icon={tf.id ? iconForCategory(tf.id) : 'layers'} label={tf.label} />
            ))}
          </div>
        </aside>

        {/* Main */}
        <main style={{ flex: 1, minWidth: 0 }}>
          {/* The triage queue is its OWN view, not a filter over the grid: these
              files are held out of the library, so a search box and tag chips
              above them would be offering to refine a set they are not in. */}
          {collectionId === 'intake' ? (
            <DamIntakeQueue data={intake} collections={collections} loading={intakeLoading}
              onChanged={() => { loadIntake(); loadSidebar(); loadAssets(); }} />
          ) : (<>
          {/* Search + tags */}
          <div style={{ display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 320px', minWidth: 240 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <Icon name="search" size={16} color={B.textLt} />
              </span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder='Search e.g. "aerial view of farmland at sunset"'
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', fontSize: font.base,
                  border: `1px solid ${B.border}`, borderRadius: radius.lg, fontFamily: 'inherit', color: B.text, outline: 'none' }} />
            </div>
            {/* Loose vs strict search matching. Loose (default): any word in the
                phrase may match. Strict: only assets containing the whole phrase. */}
            <SearchModeToggle mode={searchMode} onChange={setSearchMode} />
            <span style={{ fontSize: font.sm, color: B.textMd }}>{totalCount} asset{totalCount === 1 ? '' : 's'}</span>
          </div>

          {/* Refinement filters */}
          <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', alignItems: 'center', marginTop: space.md }}>
            <FilterSelect value={orientation} onChange={setOrientation} options={ORIENTATION_FILTERS} />
            <FilterSelect value={addedWithin} onChange={setAddedWithin} options={DATE_FILTERS} />
            <FilterSelect value={rawEdited} onChange={setRawEdited} options={RAW_EDITED_FILTERS} />
            <FilterSelect value={uploadedBy} onChange={setUploadedBy}
              options={[{ value: '', label: 'Anyone' },
                ...uploaders.map(u => ({ value: u.userID, label: `${u.name || 'Unknown'} (${u.uploadCount})` }))]} />
            {anyFilterActive && (
              <button onClick={clearFilters} style={{
                border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: font.sm, color: B.navy, fontWeight: weight.medium, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="close" size={13} color={B.navy} /> Clear filters
              </button>
            )}
          </div>

          {tags.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: space.md }}>
              {activeTag && <Chip color={B.navy} onRemove={() => setActiveTag('')}>{activeTag}</Chip>}
              {tags.filter(t => t.normName !== activeTag).slice(0, 18).map(t => (
                <Chip key={t.normName} color={B.textMd} onClick={() => setActiveTag(t.normName)}>
                  {t.name} <span style={{ opacity: 0.6 }}>{t.usageCount}</span>
                </Chip>
              ))}
            </div>
          )}

          {/* Selection toolbar */}
          {selected.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: space.md, marginTop: space.md,
              padding: '8px 14px', background: B.navy, borderRadius: radius.lg, color: B.white }}>
              <span style={{ fontWeight: weight.semibold, fontSize: font.sm }}>{selected.size} selected</span>
              <Btn size="sm" variant="secondary" onClick={bulkDownload}><Icon name="download" size={13} /> Download</Btn>
              <button onClick={() => setSelected(new Set())} style={{ ...miniBtn, color: B.white }}>
                <span style={{ fontSize: font.sm, color: B.white }}>Clear</span>
              </button>
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div style={{ padding: space.xxxl }}><Spinner /></div>
          ) : assets.length === 0 ? (
            <div style={{ marginTop: space.xxl }}>
              <EmptyState icon="image" title="No assets found"
                message={q || activeTag || type ? 'Try a different search or filter.' : 'Upload your first asset to get started.'}
                action={<Btn onClick={() => setShowUpload(true)}><Icon name="upload" size={14} /> Upload</Btn>} />
            </div>
          ) : (
            <div style={{ display: 'grid', gap: space.md, marginTop: space.lg,
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              {assets.map(a => (
                <AssetCard key={a.assetID} asset={a} selected={selected.has(a.assetID)}
                  onOpen={() => setOpenAssetId(a.assetID)} onToggleSelect={() => toggleSelect(a.assetID)}
                  onDownload={() => handleDownload(a)} />
              ))}
            </div>
          )}
          </>)}
        </main>
      </div>

      {showUpload && (
        <DamUpload collections={collections}
          defaultCollectionId={isRealCollection(collectionId) ? collectionId : ''}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { loadAssets(); loadSidebar(); }} />
      )}
      {openAssetId && (
        <DamAssetModal assetId={openAssetId} collections={collections}
          onClose={() => setOpenAssetId(null)}
          onChanged={() => { loadAssets(); loadSidebar(); }} />
      )}
      {showUploadLinks && (
        <DamShareModal mode="upload" collections={collections}
          defaultCollectionId={isRealCollection(collectionId) ? collectionId : ''}
          onClose={() => setShowUploadLinks(false)} />
      )}
      {showConnectors && (
        <DamConnectorModal collections={collections}
          defaultCollectionId={isRealCollection(collectionId) ? collectionId : ''}
          onClose={() => setShowConnectors(false)} />
      )}
      {downloadAssetTarget && (
        <DamDownloadModal asset={downloadAssetTarget} onClose={() => setDownloadAssetTarget(null)} />
      )}
      {showReset && (
        <DamResetModal onClose={() => setShowReset(false)}
          onDone={() => { loadAssets(); loadSidebar(); setPendingCount(0); }} />
      )}
    </div>
  );
}

// ── Compact filter dropdown (native select styled to match the toolbar) ──────
function FilterSelect({ value, onChange, options }) {
  const active = !!value;
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{
        padding: '7px 10px', fontSize: font.sm, fontFamily: 'inherit', cursor: 'pointer',
        border: `1px solid ${active ? B.navy : B.border}`, borderRadius: radius.lg,
        background: active ? B.navy + '0d' : B.surface, color: active ? B.navy : B.textMd,
        fontWeight: active ? weight.semibold : weight.medium, outline: 'none', maxWidth: 200,
      }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ── Search-mode toggle (Loose vs Strict) ────────────────────────────────────
// Loose  → any word in the query may match (forgiving, ranked by overlap).
// Strict → only assets whose text contains the whole phrase verbatim.
function SearchModeToggle({ mode, onChange }) {
  const opts = [
    { id: 'loose',  label: 'Any words', title: 'Match assets containing any word in your search' },
    { id: 'strict', label: 'Exact phrase', title: 'Match only assets containing the whole phrase' },
  ];
  return (
    <div style={{ display: 'inline-flex', border: `1px solid ${B.border}`, borderRadius: radius.lg, overflow: 'hidden', flexShrink: 0 }}>
      {opts.map((o, i) => {
        const active = mode === o.id;
        return (
          <button key={o.id} onClick={() => onChange(o.id)} title={o.title}
            style={{
              padding: '8px 12px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: font.sm, fontWeight: active ? weight.semibold : weight.medium,
              background: active ? B.navy : B.surface, color: active ? B.white : B.textMd,
              borderLeft: i > 0 ? `1px solid ${B.border}` : 'none', whiteSpace: 'nowrap',
            }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Sidebar item ──────────────────────────────────────────────────────────────
function SidebarItem({ active, onClick, icon, label, count, onDelete }) {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', alignItems: 'center' }}>
      <button onClick={onClick} style={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', border: 'none',
        background: active ? B.navy + '14' : 'transparent', color: active ? B.navy : B.textMd,
        borderRadius: radius.lg, cursor: 'pointer', fontFamily: 'inherit', fontSize: font.base,
        fontWeight: active ? weight.semibold : weight.medium, textAlign: 'left', minWidth: 0,
      }}>
        <Icon name={icon} size={15} color={active ? B.navy : B.textLt} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        {count != null && <span style={{ fontSize: font.xs, color: B.textLt }}>{count}</span>}
      </button>
      {onDelete && hover && (
        <button onClick={onDelete} style={miniBtn} title="Delete collection">
          <Icon name="trash" size={13} color={B.textLt} />
        </button>
      )}
    </div>
  );
}

// ── Large-file dropbox panel (admin) ────────────────────────────────────────
// Setup + status for the SharePoint dropbox: drop huge files into a synced
// folder and they import server-side (no browser upload) and move to Processed.
function DropboxPanel({ status, scanning, onScan }) {
  const folder = status?.folder || 'DAM Dropbox';
  const processed = status?.processedFolder || `${folder}/Processed`;
  const configured = status?.configured;
  const last = status?.lastScan;
  const c = last?.counts || {};
  const lastAt = last?.at ? formatInstant(last.at) : null;
  return (
    <div style={{ marginTop: space.lg, background: B.surface, border: `1px solid ${B.border}`,
      borderRadius: radius.xxl, padding: space.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.md, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="folder" size={16} color={B.navy} />
          <span style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text }}>Large-file dropbox</span>
          {configured === false && <Badge color={B.textLt}>SharePoint not configured</Badge>}
          {status?.webhook?.subscribed && <Badge color={B.textMd}>Webhook on</Badge>}
        </div>
        <Btn variant="secondary" onClick={onScan} loading={scanning} disabled={configured === false}>
          <Icon name="refresh" size={14} /> Scan dropbox
        </Btn>
      </div>
      <div style={{ marginTop: space.sm, fontSize: font.sm, color: B.textMd, lineHeight: 1.5 }}>
        For files too large for a browser upload: open the <b>{folder}</b> folder in SharePoint, choose
        <b> Sync</b> (OneDrive), and drop files into it from your computer. They import automatically
        (within ~15 min, or click <b>Scan dropbox</b>) and are moved to <b>{processed}</b> once imported.
        {' '}Sub-folders are kept — an imported file records the folder it arrived in, a folder named
        <b> Raw</b> or <b>Edited</b> classifies what is inside it, and everything lands under
        <b> Needs filing</b> until somebody puts it in a collection.
      </div>
      <div style={{ marginTop: space.sm, fontSize: font.xs, color: B.textLt, display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
        {lastAt
          ? <span>Last scan {lastAt} — {c.ingested || 0} imported, {c.started || 0} copying, {c.skippedSyncing || 0} still syncing{c.errors ? `, ${c.errors} errors` : ''}</span>
          : <span>Not scanned yet</span>}
        {status?.pendingCount ? <span>{status.pendingCount} file(s) still copying in the background</span> : null}
        {/* Two different "pending"s. The line above is bytes still moving; this
            one is copies that finished and are waiting on a person. */}
        {status?.awaitingFiling ? <span>{status.awaitingFiling} file(s) imported and waiting to be filed</span> : null}
      </div>
    </div>
  );
}

// ── Asset card ──────────────────────────────────────────────────────────────
function AssetCard({ asset, selected, onOpen, onToggleSelect, onDownload }) {
  const [hover, setHover] = useState(false);
  const cat = categoryOf(asset.fileType);
  // Prefer the stored scaled-down thumbnail; fall back to the full-res preview
  // for assets uploaded before thumbnails existed.
  const imgSrc = asset.thumbnailUrl || asset.previewUrl;
  const showImg = isImageType(asset.fileType, asset.mimeType) && imgSrc;
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onClick={onOpen}
      style={{ background: B.surface, border: `1px solid ${selected ? B.navy : B.border}`, borderRadius: radius.xxl,
        overflow: 'hidden', cursor: 'pointer', position: 'relative', boxShadow: hover ? B.shadowMd : B.shadowXs,
        transition: 'box-shadow .15s, border-color .15s' }}>
      {/* Thumb */}
      <div style={{ height: 140, background: B.surfaceSunken, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {showImg
          ? <img src={imgSrc} alt={asset.title || asset.fileName} loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <Icon name={iconForCategory(cat)} size={40} color={B.textLt} />}
      </div>

      {/* Select checkbox */}
      <button onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        style={{ position: 'absolute', top: 8, left: 8, width: 22, height: 22, borderRadius: radius.sm,
          border: `1.5px solid ${selected ? B.navy : B.white}`, background: selected ? B.navy : 'rgba(255,255,255,0.85)',
          display: (hover || selected) ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
        {selected && <Icon name="check" size={14} color={B.white} />}
      </button>

      {/* Download on hover */}
      {hover && (
        <button onClick={(e) => { e.stopPropagation(); onDownload(); }}
          style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: radius.md,
            border: 'none', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', boxShadow: B.shadowSm }} title="Download">
          <Icon name="download" size={15} color={B.navy} />
        </button>
      )}

      {/* Meta */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {asset.title || asset.fileName}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
          <Badge color={B.textMd}>{(asset.fileType || '?').toUpperCase()}</Badge>
          {asset.fileSizeBytes != null && <span style={{ fontSize: font.xs, color: B.textLt }}>{formatBytes(asset.fileSizeBytes)}</span>}
        </div>
        {asset.tags?.length > 0 && (
          <div style={{ marginTop: 6, fontSize: font.xs, color: B.textMd, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {asset.tags.slice(0, 3).join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}

const sidebarHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  fontSize: font.xs, fontWeight: weight.bold, color: B.textLt, letterSpacing: 0.6,
  textTransform: 'uppercase', padding: '0 6px 6px', marginTop: 2,
};
const miniBtn = {
  border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: radius.md,
  display: 'flex', alignItems: 'center',
};
const miniInput = {
  flex: 1, minWidth: 0, padding: '6px 8px', fontSize: font.sm, border: `1px solid ${B.border}`,
  borderRadius: radius.md, fontFamily: 'inherit', color: B.text, outline: 'none',
};
